<template>
    <div class="relativeContainer fullHeight">
        <Loader :status="loading"/>
        <div ref="containerLayoutMediationManagement" class="fullHeight scroll">
            <div class="header">
                <span class="truncate font-6">
                    <CountryFlag class="flagIconMargin" v-if="countryCode && countryName"
                                 :country-code="countryCode.toLowerCase()"/>
                    {{groupTitle}}
                    <Tooltip text="All metrics are shown by instance-GEO and not by segment" v-if="showSegmentTooltip">
                        <IconBase width="12" height="11" :icon-color="getSegmentIconColor" class="segmentWarning"
                                  icon-name="warningIcon"/>
                    </Tooltip>
                </span>
                <span class="datePicker">
                    <DateRangeStatisticsDropdown
                            v-once
                            :selected-id="selectedDateOption.id"
                            @dateRangeChanged="dateRangeChanged">
                    </DateRangeStatisticsDropdown>
                </span>
                <span @click="shouldOpenCsvExportModal() && csvMenuItemClick('exportToCsv')">
                    <IconMenu :menu-items="exportCsvItems"
                              unique-id="csvMenu"
                              trigger="click"
                              :disabled="shouldOpenCsvExportModal()"
                              @actionClicked="csvMenuItemClick">
                        <button type="button"
                                class="icon-button exportToCsv">
                            <IconBase v-once width="14" height="15" icon-name="exportCsvIcon"
                                      :icon-color="this.getExportCsvIconColor"/>
                        </button>
                    </IconMenu>
                </span>
                <span class="marginRight">
                    <HelpMenu @toggleHelpMenu="helpMenuShown = $event">
                        <IconBase slot="icon" width="17" height="17" icon-name="helpIcon"
                                  :icon-color="getIconColor" :iconHoverColor="getIconHoverColor" />
                        <div slot="content" v-once>
                            <div v-for="option in helpMenuOptions" :key="option.title" class="helpMenuOption">
                                <div class="title font-9">
                                    {{option.title}}
                                </div>
                                <div class="description font-7">
                                    {{option.description}}
                                </div>
                            </div>
                            <div class="link">
                                <a class="font-7 linkStyle-secondary" :href="helpLink" target="_blank">Read more</a>
                            </div>
                        </div>
                    </HelpMenu>
                </span>
            </div>
            <div id="bidders" v-if="showBidderSection" class="container">
                <BiddingTable :bidderInstances="biddersInstances"
                              :appKey="appKey"
                              :groupHasChina="getChinaLogic"
                              :maxSov="maxSov"/>
            </div>
            <div class="container" id="tiers">
                <div v-for="(tier, index) in tiers" :key="tier.id" class="tier">
                    <Tier :tier="tier"
                          :parentComponent="$refs.containerLayoutMediationManagement"
                          :hasActiveBidders="biddersInstances.length > 0"
                          :countryLevel="!!countryCode"
                          :startCounter="tiersStartIndex[index]"
                          :allowRemoveFromGroup="instancesAmount > 1"
                          :isDragging="tier.id === tierIdOnDrag"
                          :disableChangeTier="disableChangeTier"
                          :appKey="appKey"
                          :tierIsUpdating="tier.id === onUpdateData.tierId"
                          :instanceKeyToUpdate="onUpdateData.instanceKey"
                          :viewOnly="viewOnly"
                          :groupHasChina="getChinaLogic"
                          :adUnit="adUnit"
                          :maxSov="maxSov"
                          @typeChanged="tierTypeChange(tier, $event)"
                          @menuAction="tierMenuAction(tier, $event)"
                          @instancePositionChanged="instancePositionChange"
                          @tierOnDrag="tierOnDrag"
                          @rateChanged="onRateChange"
                    />
                </div>
            </div>
            <div v-if="!!backfill" id="backfill" class="container">
                <InstanceWaffle
                        class="backfill"
                        :providerName="backfill.providerDisplayName"
                        :name="backfill.instanceName"
                        :is-locked="true"
                        :impressions="backfill.impressions"
                        :ecpm="backfill.eCPM"
                        :activeCountries="backfill.activeCountries"
                        :isAllCountries="backfill.isAllCountries"
                        :hasCapping="backfill.iterationAdCap !== null"
                        :tagType="backfill.tagType"
                        :tagId="backfill.tagId"
                        :appKey="appKey"
                        :showCappingInMenu="false"
                        :showMenu="!viewOnly"
                        :showRate="false"
                        :instanceId="backfill.instanceId"
                        :providerId="backfill.providerId"
                        :viewOnly="viewOnly"
                        :maxSov="maxSov"
                        @menuAction="tierMenuAction(null, $event)"
                />
            </div>
            <div class="footer container"></div>
        </div>
        <NoDataOverlay :isShown="showNoData"
                        main-text="No Ad Sources Defined for this Country/Group">
            <span slot="subText">
                <span>To add an Ad Source go to </span>
                <a :href="noDataLink">SDK Networks</a>
            </span>
        </NoDataOverlay>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import CacheStore from 'services/utils/cacheStore';
import { lowerFirstLetter } from 'services/utils/strings';
import Links from 'services/metaData/links';
import Auth from 'services/auth';
import CountryFlag from 'components/countryFlag/countryFlag.vue';
import Loader from 'components/loader/loader.vue';
import NoDataOverlay from 'components/noDataOverlay/noDataOverlay.vue';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import IconMenu from 'components/iconMenu/IconMenu.vue';
import HelpMenu from './components/helpMenu/helpMenu.vue';
import DateRangeStatisticsDropdown from './components/DateRangeStatisticsDropdown/DateRangeStatisticsDropdown.vue';
import BiddingTable from './components/biddingTable/biddingTable.vue';
import Tier from './components/Tier/Tier.vue';
import TiersService from './services/tiers';
import RatesService from './services/rate';
import mediationManagementConsts from './consts';
import InstanceWaffle from './components/InstanceWaffle/InstanceWaffle.vue';
import Statistics from './services/statistics/statistics';

const cacheStoreDatesKey = 'cacheStoreDates';

export default {
    name: 'MediationManagementLayout',
    components: {
        Tooltip,
        NoDataOverlay,
        InstanceWaffle,
        Loader,
        CountryFlag,
        BiddingTable,
        HelpMenu,
        IconBase,
        DateRangeStatisticsDropdown,
        Tier,
        IconMenu
    },
    props: {
        appKey: {
            type: String,
            required: true
        },
        groupId: {
            type: String,
            required: true
        },
        testGroup: {
            type: String,
            required: true
        },
        groupName: {
            type: String,
            required: true
        },
        countryCode: {
            type: String,
            required: false
        },
        countryName: {
            type: String,
            required: false
        },
        adUnit: {
            type: String
        },
        disableChangeTier: {
            type: Boolean,
            required: true
        },
        showSegmentTooltip: {
            type: Boolean
        },
        groupHasChina: {
            type: Boolean
        },
        hasAb: {
            type: Boolean
        }
    },
    data() {
        return {
            startDate: '',
            endDate: '',
            showHelpMenu: true,
            tiersStartIndex: [],
            biddersInstances: [],
            instancesAmount: 0,
            tiers: [],
            backfill: null,
            loading: false,
            showNoData: false,
            tierIdOnDrag: null,
            onUpdateData: {},
            showBidderSection: false,
            viewOnly: false,
            helpMenuShown: false,
            cacheStoreDates: null,
            loadingTier: null,
            exportCsvItems: mediationManagementConsts.exportCsvItems,
            maxSov: 0
        };
    },
    computed: {
        getIconColor() {
            return !this.helpMenuShown ? consts.COLORS.LIGHT_700 : consts.COLORS.LIGHT_900;
        },
        getIconHoverColor() {
            return consts.COLORS.LIGHT_900;
        },
        getSegmentIconColor() {
            return consts.COLORS.NEUTRAL_700;
        },
        getExportCsvIconColor() {
            return consts.COLORS.WHITE;
        },
        helpLink() {
            return Links.rate;
        },
        helpMenuOptions() {
            return mediationManagementConsts.helpMenuOptions;
        },
        selectedDateOption() {
            return this.cacheStoreDates.getters.getFromCache(cacheStoreDatesKey, '');
        },
        noDataLink() {
            return `/partners/monetize/mediation/setup/app/${this.appKey}`;
        },
        groupTitle() {
            const countryAddition = this.countryName && this.countryCode ? `${this.countryName} • ` : '';
            return `${countryAddition}${this.groupName}`;
        },
        deliveryType() {
            return mediationManagementConsts.adUnitToDeliveryType[this.adUnit];
        },
        getChinaLogic() {
            return this.countryCode ? false : this.groupHasChina;
        }
    },
    methods: {
        shouldOpenCsvExportModal() {
            // open export modal instead of menu if the waterfall is not editable,
            // or has long AB (currently not supported by import)
            return this.hasAb || this.disableChangeTier;
        },
        dateRangeChanged(item) {
            this.startDate = item.startDay;
            this.endDate = item.endDay;
            this.cacheStoreDates.commit({
                type: 'setCache',
                api: cacheStoreDatesKey,
                req: '',
                data: item
            });
            this.getData();
        },
        async tierTypeChange(tierData, newTierType) {
            const params = {
                appKey: this.appKey,
                startDate: this.startDate,
                endDate: this.endDate,
                testGroup: this.testGroup,
                id: tierData.id,
                type: newTierType.id,
                deliveryType: this.deliveryType,
                groupId: this.groupId
            };

            try {
                this.loading = true;
                await TiersService.updateTierType(params);
                await this.getData(true);
            } catch (e) {
                this.handleError('Error occurred while updating tier.');
            }
        },
        async tierMenuAction(tier, data) {
            if (data.actionData === 'remove') {
                this.loading = true;
                try {
                    const params = {
                        appKey: this.appKey,
                        instanceId: data.instance.instanceId,
                        tagId: data.instance.tagId,
                        providerId: data.instance.providerId,
                        deliveryType: this.deliveryType,
                        groupId: this.groupId,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        testGroup: this.testGroup
                    };
                    if (tier) {
                        params.tierId = tier.id;
                    }
                    const updatedData = await TiersService.removeInstanceFromGroup(params);
                    const dataWithSov = Statistics.calcSOV(updatedData.data, { sumAlsoTierInstances: true });
                    this.maxSov = dataWithSov.maxSov;
                    this.formatData(dataWithSov);
                } catch (e) {
                    this.handleError('Error occurred while removing from tier.');
                } finally {
                    this.loading = false;
                }
            }

            this.$emit('menuAction', data);
        },
        async getData(refresh = false, turnOnLoading = true) {
            if (turnOnLoading) {
                this.loading = true;
            }
            const params = {
                appKey: this.appKey,
                startDate: this.startDate,
                endDate: this.endDate,
                groupId: this.groupId,
                testGroup: this.testGroup,
                deliveryType: this.deliveryType,
                refresh
            };

            if (this.countryCode) {
                params.country = this.countryCode;
            }

            try {
                const { data } = await TiersService.getTiersAndInstances(params);
                data.bidders = data.bidders || [];
                data.tiers = data.tiers || [];
                const dataWithSov = Statistics.calcSOV(data, { sumAlsoTierInstances: true });
                this.maxSov = dataWithSov.maxSov;
                this.formatData(dataWithSov);
            } catch (err) {
                this.handleError('Error occurred while getting group data.');
            } finally {
                this.showNoData = this.instancesAmount === 0 && this.biddersInstances.length === 0 && !this.backfill;
                this.loading = false;
            }
        },
        formatData({ bidders, tiers, backfill }) {
            this.biddersInstances = bidders || [];
            this.tiers = tiers || [];
            this.backfill = backfill || null;
            this.instancesAmount = this.tiers.reduce(
                (acc, tier) => acc + (tier.instances ? tier.instances.length : 0),
                0
            );
            this.instancesAmount += (this.biddersInstances.length);
            if (backfill) {
                this.instancesAmount += 1;
            }

            let currentIndex = 0;
            this.tiersStartIndex = this.tiers.map((tier) => {
                const index = currentIndex;
                currentIndex += tier.instances ? tier.instances.length : 0;
                return index;
            });
        },
        handleError(errorMessage) {
            this.$emit('handleError', errorMessage);
        },
        async instancePositionChange(event) {
            const { instance, manualPosition, tierId } = event;
            delete instance.manualPosition;

            const params = {
                ...instance,
                tierId,
                startDate: this.startDate,
                endDate: this.endDate,
                manualPosition,
                testGroup: this.testGroup,
                appKey: this.appKey,
                groupId: this.groupId,
                deliveryType: this.deliveryType
            };
            if (this.countryCode) {
                params.country = this.countryCode;
            }

            try {
                this.onUpdateData = {
                    tierId,
                    instanceKey: `${instance.providerId}_${instance.instanceId}_${instance.tagId}`
                };
                const updatedData = await TiersService.updateInstanceConfig(params);
                const dataWithSOV = Statistics.calcSOV(updatedData.data, { sumAlsoTierInstances: true });
                this.maxSov = dataWithSOV.maxSov;
                this.formatData(dataWithSOV);
            } catch (e) {
                this.handleError('Error updating instance in tier.');
            } finally {
                this.onUpdateData = {};
            }
        },
        tierOnDrag(tierId) {
            if (this.tierIdOnDrag !== tierId) {
                this.tierIdOnDrag = tierId;
            }
        },
        async onRateChange(data) {
            try {
                this.onUpdateData = {
                    tierId: data.tierId,
                    instanceKey: `${data.providerId}_${data.instanceId}_${data.tagId}`
                };
                if (data.value) {
                    await RatesService.update({
                        aniId: data.aniId,
                        tagId: data.tagId,
                        groupId: data.groupId,
                        countryCode: this.countryCode,
                        rateValue: data.value,
                        appKey: this.appKey
                    });
                } else {
                    await RatesService.deleteLevel({
                        aniId: data.aniId,
                        tagId: data.tagId,
                        groupId: data.groupId,
                        countryCode: this.countryCode,
                        appKey: this.appKey
                    });
                }

                await this.getData(true, false);
                this.onUpdateData = {};
                this.$emit('rateChanged', data);
            } catch (e) {
                this.handleError('Error updating rate');
                this.loading = false;
            }
        },
        csvMenuItemClick(actionData) {
            const info = {
                bidders: this.biddersInstances,
                tiers: this.tiers,
                backfill: this.backfill
            };
            const csvData = this.formatCsvData(info);

            const data = {
                actionData,
                timeframe: this.cacheStoreDates.getters.getFromCache(cacheStoreDatesKey, ''),
                groupName: this.groupName,
                adUnit: this.adUnit,
                csvData
            };

            this.$emit('csvMenuItemClick', data);
        },
        formatCsvData(data) {
            const dataWithSov = Statistics.calcSOV(data, { sumAlsoTierInstances: true });
            this.maxSov = dataWithSov.maxSov;
            const headers = mediationManagementConsts.exportToCsvHeaders;
            const formattedBidders = this.getFormattedBidders(dataWithSov, Object.keys(headers));
            const formattedTiers = this.getFormattedTiers(dataWithSov, Object.keys(headers));
            const formattedBackfill = this.getFormattedBackfill(dataWithSov,
                Object.keys(headers),
                formattedTiers.length);
            return [Object.values(headers), ...formattedBidders, ...formattedTiers, formattedBackfill].join('\r\n');
        },
        formatCsvInstance(headers, instance) {
            return headers.map((header) => `"${instance[header] || ''}"`).join(',');
        },
        getFormattedBidders(dataWithSov, headers) {
            return dataWithSov.bidders.map((bidder) => this.formatCsvInstance(headers,
                {
                    ...bidder,
                    ...{
                        tierType: '-',
                        providerDisplayName: `${lowerFirstLetter(bidder.providerDisplayName)} bidding`,
                        instanceName: '-',
                        rate: '-',
                        rank: '-',
                        waterfallTier: 'Bidding'
                    }
                }));
        },
        getFormattedTiers(dataWithSov, headers) {
            const getProviderName = (instance) => {
                if (instance.tagId) {
                    return consts.TAG_TYPE_TO_NAME[instance.tagType];
                }

                return lowerFirstLetter(instance.providerDisplayName);
            };

            const result = [];
            let rank = 1;
            dataWithSov.tiers.forEach((tier) => {
                if (tier && tier.instances && tier.instances.length > 0) {
                    const tierType = mediationManagementConsts.TIER_TYPE_TO_NAME[tier.type];
                    const waterfallTier = `Tier ${tier.position}`;

                    tier.instances.forEach((instance) => {
                        const formattedInstance = this.formatCsvInstance(headers,
                            {
                                ...instance,
                                ...{
                                    tierType,
                                    waterfallTier,
                                    rank,
                                    providerDisplayName: getProviderName(instance),
                                    rate: this.getRateCsv(instance.rates)
                                }
                            });
                        result.push(formattedInstance);
                        rank += 1;
                    });
                }
            });
            return result;
        },
        getFormattedBackfill(dataWithSov, headers, rank) {
            return dataWithSov.backfill
                ? this.formatCsvInstance(headers, {
                    ...dataWithSov.backfill,
                    ...{
                        waterfallTier: 'Backfill',
                        rank: rank + 1,
                        tierType: '-',
                        instanceName: '-',
                        rate: '-'
                    }
                })
                : [];
        },
        getRateCsv(rates) {
            if (rates) {
                if (this.countryCode && rates.country) {
                    return rates.country;
                }
                if (rates.group) {
                    return rates.group;
                }
                if (rates.global) {
                    return rates.global;
                }
            }
            return '-';
        }
    },
    mounted() {
        this.getData();
    },
    created() {
        this.cacheStoreDates = CacheStore.getStore(cacheStoreDatesKey);
        const cachedResult = this.cacheStoreDates.getters.getFromCache(cacheStoreDatesKey, '');
        if (!cachedResult) {
            const datesOptionSelect = mediationManagementConsts.datesOptions[1];
            this.cacheStoreDates.commit({
                type: 'setCache',
                api: cacheStoreDatesKey,
                req: '',
                data: datesOptionSelect
            });
            this.startDate = datesOptionSelect.startDay;
            this.endDate = datesOptionSelect.endDay;
        } else {
            this.startDate = cachedResult.startDay;
            this.endDate = cachedResult.endDay;
        }

        this.$on('getData', () => {
            this.getData(true);
        });
        Auth.isAuthorized([`isAllowAuction${this.adUnit.toUpperCase()}`]).then((result) => {
            this.showBidderSection = result;
        });
        Auth.isAuthorized(['viewer']).then((result) => {
            this.viewOnly = result;
        });
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    @import "~styles/buttons";

    $height: 47px;

    .header {
        padding-left: 16px;
        border-bottom: $border;
        line-height: $height;
        display: grid;
        grid-gap: 8px;
        grid-template-columns: auto 150px 25px 30px;
    }

    .marginRight {
        margin-right: 8px;
    }

    .helpMenuOption {
        padding-top: 12px;

        .title {
            border-radius: 10px;
            background-color: $light-300;
            color: #758ba5;
            width: fit-content;
            line-height: 20px;
            margin: 4px 5px;
            padding: 1px 5px;
        }

        .description {
            line-height: 20px;
            color: $light-900;
            letter-spacing: normal;
            font-stretch: normal;
            padding: 4px 5px;
        }
    }

    .link {
        padding-left: 5px;
    }

    .flagIconMargin {
        margin-right: 4px;
    }

    .tierMargin {
        margin-top: 8px;
        margin-bottom: 8px;
    }

    .noDataHeight {
        min-height: 200px;
    }

    .container {
        padding-left: 16px;
        display: grid;
    }

    .segmentWarning {
        margin-left: 6px;
    }

    .backfill {
        border-bottom: $border;
    }

    .datePicker {
        margin-top: -2px;
    }
    .footer {
        height: 109px;
    }

    .scroll {
        overflow-y: scroll;
        @include customScroll;
    }

    .helpIcon {
        line-height: 16px;
        margin-left: 2px;
        vertical-align: middle;
    }

    .exportToCsv {
        &.icon-button {
            background: $primary;
            border: none;
            margin-top: -2px;

            /deep/ svg {
                margin-top: 1px;
            }
        }
    }
</style>
